/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import {
  selectEmbedToken,
  selectHotelName,
  selectTabList,
  selectTargetDate,
} from 'modules/dashboard/selectors';
import { EXPORT_OPTIONS, REPORT_TYPE } from 'modules/dashboard/constants';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import ExportToCSV, {
  getPbiExportData,
} from 'modules/dashboard/components/tab-container/export-visual-data/export-csv';
import { AssistantIcon } from 'modules/common/components';
import { selectAuthUser } from 'modules/common/auth/selectors';
import { isEmpty } from '../../../../../../../../common/helpers/object';
import { paceActions } from '../../../../slice';
import {
  selectActivePaceTab,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectPaceAlignmentToggle,
  selectPaceCumulativeView,
  selectPaceHotelCumulativeTabularVisuals,
  selectPaceHotelLagAlignedCumulativeTabularVisuals,
  selectPaceHotelLagAlignedTabularVisuals,
  selectPaceHotelTabularFilterList,
  selectPaceHotelTabularVisuals,
  selectPaceLagAligned,
  selectPaceSymmetricComparisonToggle,
  selectRevenueIsDisplayPH,
} from '../../../../selectors';

/**
 * Pace Hotel tabular visual report with filters
 */
const PaceHotelTabularWidget = ({ setIsHotelLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const tabularFilterList = useSelector(selectPaceHotelTabularFilterList);
  const alignmentToggle = useSelector(selectPaceAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const tokenDetails = useSelector(selectEmbedToken);
  const paceLagAligned = useSelector(selectPaceLagAligned);
  const paceCumulative = useSelector(selectPaceCumulativeView);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayPH);
  const paceHotelVisuals = useSelector(selectPaceHotelTabularVisuals);
  const paceHotelLagAlignedVisuals = useSelector(selectPaceHotelLagAlignedTabularVisuals);
  const paceHotelAbsoluteCumulativeVisuals = useSelector(selectPaceHotelCumulativeTabularVisuals);
  const TAB_VALUE = useSelector(selectTabList);
  const paceHotelCumulativeLagAlignedVisuals = useSelector(
    selectPaceHotelLagAlignedCumulativeTabularVisuals
  );
  //
  const targetDate = useSelector(selectTargetDate);
  const activeTab = useSelector(selectActivePaceTab);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const currentUser = useSelector(selectAuthUser);
  const selectedHotel = useSelector(selectHotelName);
  //
  const [tabularCombined, setTabularCombined] = useState({});
  const [isTabularCombinedRendered, setIsTabularCombinedRendered] = useState({});
  const [isVisualProcessing, setIsVisualProcessing] = useState(false);
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const trendFilters = `Alignment : , ${
    alignmentToggle ? 'On' : 'Off'
  }\r\nSymmetric Comparison : , ${
    symmetricComparisonToggle ? 'On' : 'Off'
  }\r\nCompared Hotels: , "${hotelFocusOn?.label}"\r\n`;
  // To get visual details
  const getVisual = (index) => {
    let found;
    if (paceCumulative && !paceLagAligned) {
      found = paceHotelAbsoluteCumulativeVisuals?.find((visual) => visual?.order === index);
    } else if (paceLagAligned && !paceCumulative) {
      found = paceHotelLagAlignedVisuals?.find((visual) => visual?.order === index);
    } else if (paceLagAligned && paceCumulative) {
      found = paceHotelCumulativeLagAlignedVisuals?.find((visual) => visual?.order === index);
    } else {
      found = paceHotelVisuals?.find((visual) => visual?.order === index);
    }
    return found;
  };
  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = tabularFilterList
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(r) => setIsLoaded(r)}
          setIsRendered={(d) => setIsRendered(d)}
          tab={TAB_VALUE.PACE}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (order) => getVisual(order)?.name;
  //
  const getExcelExportData = async () => {
    const data = await getPbiExportData({
      isLoaded: isTabularCombinedRendered,
      fileName:
        paceCumulative && paceLagAligned
          ? 'PVH_Cumulative_Lag_Aligned'
          : paceCumulative && !paceLagAligned
          ? 'PVH_Cumulative'
          : paceLagAligned && !paceCumulative
          ? 'PVH_Lag_Aligned'
          : 'PVH_Daily',
      currentUser,
      selectedHotel,
      report: tabularCombined,
      isInterpreterAgent: true,
    });
    return data;
  };
  //
  useEffect(() => {
    setIsTabularCombinedRendered(false);
  }, [paceCumulative, paceLagAligned]);
  //
  return (
    !isEmpty(tabularFilterList) && (
      <VisualGrid
        isDisplay={revenueIsDisplay}
        visualName={getVisualName(0)}
        visual={getWidget(
          0,
          styles.tabularVisual,
          setTabularCombined,
          setIsHotelLoaded,
          setIsTabularCombinedRendered,
          paceActions.setRevenueIsDisplayPH
        )}
        onClick={() => {
          dispatch(paceActions.setRevenueIsDisplayPH(!revenueIsDisplay));
        }}
        toggleFragment={
          <ExportToCSV
            exportExcel
            report={tabularCombined}
            isLoaded={isTabularCombinedRendered}
            fileName={
              paceCumulative && paceLagAligned
                ? 'PVH_Cumulative_Lag_Aligned'
                : paceCumulative && !paceLagAligned
                ? 'PVH_Cumulative'
                : paceLagAligned && !paceCumulative
                ? 'PVH_Lag_Aligned'
                : 'PVH_Daily'
            }
            otherFilters={trendFilters}
          />
        }
        isWorkbookHotelComparison={activeTab === REPORT_TYPE.PACE_HOTEL_WIDGET}
        comparisonHotel={hotelFocusOn}
        displayHotelName
        interpreterFragment={
          <AssistantIcon
            date={targetDate}
            visualName={getVisualName(0)}
            report={tabularCombined}
            isProcessing={isVisualProcessing}
            setIsProcessing={setIsVisualProcessing}
            disabled={!isTabularCombinedRendered || isVisualProcessing || !revenueIsDisplay}
            enableSecondaryHotelGroups={enableSecondaryHotelGroups}
            getExcelExportData={getExcelExportData}
            exportType={exportType}
            setExportType={setExportType}
          />
        }
      />
    )
  );
};
//
export default PaceHotelTabularWidget;
